<template>
  <ui-collapse
    class="price-filter"
    data-test="filter-and-sort-price"
    :class="{ disabled: disabled }"
    :selected="priceAvailable"
    :collapse-id="`collapse-filter-and-sort-price`"
    @collapse-open="showSlider = true"
    @collapse-close="showSlider = false"
  >
    <template #ui-collapse-title>
      <loading-spinner v-if="disabled" />
      <translate v-else> Price </translate>
      <span v-if="maxPrice && !disabled" class="summary">
        <pu-price-rounded :price="minRangePrice" /> -
        <pu-price-rounded :price="maxRangePrice" />
      </span>
    </template>
    <template #ui-collapse-body>
      <template v-if="showSlider">
        <filter-and-sort-price-slider
          :min-price="minPrice"
          :max-price="maxPrice"
          :price-histogram-data="priceHistogramData"
          :price-range="priceRange"
          :disabled="!priceAvailable"
          @update-range="updateRange"
        />
      </template>
      <div v-if="showResetButton" class="reset-price">
        <base-button css-class="tiny icon minimal" @click="resetPriceFilter">
          <img loading="lazy" src="@/assets/images/refresh.svg" class="icon" />
          <translate>Reset price</translate>
        </base-button>
      </div>
    </template>
  </ui-collapse>
</template>
<script lang="ts">
import 'vue-slider-component/theme/default.css'
import { DateRange } from '~/utility/date/DateRange'
import { Money } from '~/utility/money'
import type { PriceHistogram } from '~/apps/search/schemas'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'

@Component({
  components: {
    'vue-slider': defineAsyncComponent(() => import('vue-slider-component')),
  },
  emits: ['change'],
})
class FilterAndSortPrice extends Vue {
  @Prop()
  dates: { arrive: string; depart: string }

  @Prop()
  minPrice: number

  @Prop()
  maxPrice: number

  @Prop()
  priceHistogramData: PriceHistogram

  @Prop()
  disabled: boolean

  @Prop({ required: false })
  selectedPriceRange?: [number, number]

  get showResetButton() {
    if (
      this.selectedPriceRange &&
      (this.selectedPriceRange[0] > 0 ||
        this.selectedPriceRange[1] < this.maxPrice)
    ) {
      return true
    }
    return false
  }

  showSlider = false

  @Watch('maxPrice')
  maxPriceChanged() {
    if (this.selectedPriceRange && this.maxPrice < this.selectedPriceRange[1]) {
      this.change([this.selectedPriceRange[0], this.maxPrice], false)
    }
  }

  get priceRange() {
    const maxPrice = Math.ceil(this.maxPrice / 100) * 100
    const minPrice = Math.ceil(this.minPrice / 100) * 100
    if (maxPrice === 0) {
      return [0, 0]
    }
    const range = this.selectedPriceRange
    const range0 = range && range[0] > minPrice ? range[0] : minPrice
    const range1 = range && range[1] < maxPrice ? range[1] : maxPrice
    return [range0, range1]
  }

  get priceAvailable() {
    return !isNaN(this.priceRange[1])
  }

  get nights() {
    return new DateRange(this.dates.arrive, this.dates.depart).nights
  }

  get minRangePrice() {
    return Money({ amount: Math.round(this.priceRange[0]), currency: 'GBP' })
  }

  get maxRangePrice() {
    return Money({ amount: Math.round(this.priceRange[1]), currency: 'GBP' })
  }

  updateRange(range: number[]) {
    if (range.toString() !== this.priceRange.toString()) {
      this.change([range[0], range[1]], true)
    }
  }

  resetPriceFilter() {
    this.change([], true)
  }

  change(value: number[], manual: boolean) {
    const change = { range: { min: value[0], max: value[1] }, manual }
    this.$emit('change', change)
    return change
  }
}

export default toNative(FilterAndSortPrice)
</script>

<style scoped>
.summary {
  display: block;
  font-weight: normal;
  color: var(--colour-text-light);
}

.summary :deep(.display-price) {
  font-weight: normal;
}

.price-filter :deep(.ui-collapse-content) {
  padding: 1em 1em 1.4em;
}

.price-filter.disabled {
  opacity: 0.75;
  filter: grayscale(1);
  pointer-events: none;
}

.reset-price {
  display: flex;
  justify-content: center;
}
</style>
